<template>
  <div v-if="organisations.length > 0" ref="switcherRoot" class="relative">
    <button
      class="flex items-center gap-2 px-3 py-1.5 text-sm text-charcoal-200 hover:text-white hover:bg-charcoal-700 rounded-lg transition-colors"
      @click="isOpen = !isOpen"
    >
      <svg class="w-4 h-4 text-charcoal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
        />
      </svg>
      <span class="max-w-[120px] truncate">{{ currentOrgName }}</span>
      <svg class="w-3 h-3 text-charcoal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <div
      v-if="isOpen"
      class="absolute left-0 mt-2 w-64 bg-charcoal-800 rounded-md shadow-lg ring-1 ring-charcoal-600 z-50"
    >
      <div class="px-3 py-2 border-b border-charcoal-600">
        <p class="text-xs text-charcoal-400 uppercase tracking-wide">Organisation</p>
      </div>
      <div class="py-1 max-h-60 overflow-y-auto">
        <button
          v-for="org in organisations"
          :key="org.id"
          class="w-full text-left px-4 py-2 text-sm hover:bg-charcoal-700 flex items-center justify-between"
          :class="org.id === currentOrgId ? 'text-brand-400' : 'text-charcoal-200'"
          @click="switchOrg(org)"
        >
          <span class="truncate">{{ org.name }}</span>
          <svg
            v-if="org.id === currentOrgId"
            class="w-4 h-4 text-brand-400 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M5 13l4 4L19 7"
            />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useAuthStore } from '@/stores/auth.store'

interface UserOrganisation {
  id: string
  name: string
  slug: string
  auth0_org_id: string | null
  role: string
}

const authStore = useAuthStore()

const switcherRoot = ref<HTMLElement | null>(null)
const isOpen = ref(false)
const organisations = ref<UserOrganisation[]>([])
const currentOrgId = ref<string | null>(localStorage.getItem('sc_current_org_id'))

const currentOrgName = computed(() => {
  const org = organisations.value.find(o => o.id === currentOrgId.value)
  return org?.name || organisations.value[0]?.name || 'Select Org'
})

async function fetchUserOrgs() {
  try {
    const apiBase = import.meta.env.VITE_BFF_URL || 'http://localhost:3001/api'
    const tokenResult = await authStore.getAccessToken()
    if (!tokenResult.ok) return

    const res = await fetch(`${apiBase}/user/organisations`, {
      headers: { Authorization: `Bearer ${tokenResult.value}` },
    })
    if (!res.ok) return

    const data = await res.json()
    organisations.value = data.organisations || []

    // If the user has no organisations, they have no access — force logout
    if (organisations.value.length === 0) {
      await authStore.logout({ returnTo: window.location.origin })
      return
    }

    // Default to first org if none selected
    if (!currentOrgId.value && organisations.value.length > 0) {
      const firstOrg = organisations.value[0]!
      currentOrgId.value = firstOrg.id
      localStorage.setItem('sc_current_org_id', firstOrg.id)
    }
  } catch {
    // Silently fail — org switcher is optional until orgs are enabled
  }
}

async function switchOrg(org: UserOrganisation) {
  isOpen.value = false
  if (org.id === currentOrgId.value) return

  currentOrgId.value = org.id
  localStorage.setItem('sc_current_org_id', org.id)

  // If org has Auth0 org ID, re-authenticate with that org for org-scoped token
  if (org.auth0_org_id) {
    await authStore.login({
      organization: org.auth0_org_id,
      appState: { returnTo: window.location.pathname },
    })
  } else {
    // No Auth0 org — just refresh the page to pick up new org context
    window.location.reload()
  }
}

function handleClickOutside(event: Event) {
  const target = event.target as HTMLElement
  if (switcherRoot.value && !switcherRoot.value.contains(target)) {
    isOpen.value = false
  }
}

onMounted(() => {
  fetchUserOrgs()
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>
